import React from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { RootState } from "../../store/Index";
import muzukurulogo from "../../assets/muzukurublack.png";

const SECTIONS: { title: string; body: React.ReactNode }[] = [
  {
    title: "1. Acceptance of Terms",
    body: (
      <>
        These Terms of Service ("Terms") govern your access to and use of
        Muzukuru Streaming Services ("Muzukuru", "we", "us", "our"),
        including our booking, livestreaming, and memorial page platform. By
        creating an account, making a booking, or otherwise using our
        services, you agree to be bound by these Terms. If you do not agree,
        please do not use the platform.
      </>
    ),
  },
  {
    title: "2. Our Services",
    body: (
      <>
        Muzukuru provides tools for booking, coordinating, and livestreaming
        funeral and memorial events, along with related services such as
        memorial pages, crew and venue coordination, and event
        communications. We may add, change, or discontinue features at any
        time.
      </>
    ),
  },
  {
    title: "3. Accounts",
    body: (
      <>
        You are responsible for maintaining the confidentiality of your
        account credentials and for all activity that occurs under your
        account. You must provide accurate information when registering or
        making a booking, and notify us promptly of any unauthorized use of
        your account.
      </>
    ),
  },
  {
    title: "4. Bookings and Payments",
    body: (
      <>
        <Box component="ul" sx={{ pl: 3, mt: 0 }}>
          <li>
            Bookings are confirmed once payment (in full or as a deposit, per
            the applicable package) has been received and acknowledged.
          </li>
          <li>
            Prices, packages, and add-ons are as displayed at the time of
            booking and are subject to change for future bookings.
          </li>
          <li>
            Cancellations, rescheduling, and refunds are handled on a
            case-by-case basis given the time-sensitive nature of funeral
            services — contact your account representative as soon as
            possible if a change is needed.
          </li>
          <li>
            Payments are processed through third-party payment providers; we
            are not responsible for issues arising from those providers'
            systems.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "5. Livestreaming and Content",
    body: (
      <>
        <Box component="ul" sx={{ pl: 3, mt: 0 }}>
          <li>
            You are responsible for the accuracy and appropriateness of
            content you submit, including names, photos, tributes, and
            comments associated with a booking or memorial page.
          </li>
          <li>
            Streaming quality depends on factors outside our control,
            including venue connectivity and third-party platforms (e.g.
            YouTube) used for distribution.
          </li>
          <li>
            We reserve the right to remove content that is unlawful,
            abusive, or violates these Terms.
          </li>
          <li>
            Access to a livestream or memorial page may be public or
            restricted, depending on the privacy setting chosen for that
            booking.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "6. Acceptable Use",
    body: (
      <>
        You agree not to use the platform to: violate any law; infringe on
        the rights of others; upload harmful or malicious content; attempt to
        gain unauthorized access to our systems; or interfere with the
        proper functioning of the platform.
      </>
    ),
  },
  {
    title: "7. Third-Party Services",
    body: (
      <>
        Our platform integrates with third-party services (such as payment
        processors, messaging providers, and video platforms) to deliver
        certain features. Your use of those integrations is also subject to
        the respective third party's terms and policies.
      </>
    ),
  },
  {
    title: "8. Intellectual Property",
    body: (
      <>
        The Muzukuru platform, branding, and underlying software are owned
        by Muzukuru Streaming Services. Content you submit (photos, tributes,
        recordings) remains yours; by submitting it, you grant us a license
        to host, process, and display it as needed to provide the service.
      </>
    ),
  },
  {
    title: "9. Limitation of Liability",
    body: (
      <>
        To the fullest extent permitted by law, Muzukuru is not liable for
        indirect, incidental, or consequential damages arising from your use
        of the platform, including service interruptions, streaming quality
        issues, or third-party provider failures. Our total liability for
        any claim is limited to the amount you paid for the booking giving
        rise to the claim.
      </>
    ),
  },
  {
    title: "10. Termination",
    body: (
      <>
        We may suspend or terminate your access to the platform if you
        violate these Terms or misuse the service. You may stop using the
        platform at any time; provisions that by their nature should survive
        (e.g. payment obligations, intellectual property, liability limits)
        will continue to apply.
      </>
    ),
  },
  {
    title: "11. Changes to These Terms",
    body: (
      <>
        We may update these Terms from time to time. Changes take effect
        once posted on this page. Continued use of the platform after an
        update constitutes acceptance of the revised Terms.
      </>
    ),
  },
  {
    title: "12. Contact Us",
    body: (
      <>
        If you have questions about these Terms, please reach out via our{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          Contact
        </Box>{" "}
        page or your account representative.
      </>
    ),
  },
];

const TermsOfService: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: colors.primary[400], minHeight: "100vh" }}>
      <Box sx={{ maxWidth: "860px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 6, md: 10 } }}>
        <Box
          component="img"
          onClick={() => navigate("/")}
          src={muzukurulogo}
          alt="Muzukuru logo"
          sx={{ width: { xs: "140px", sm: "160px" }, height: "auto", cursor: "pointer", mb: 5 }}
        />

        <Typography
          sx={{
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            fontWeight: 800,
            color: colors.primary[900],
            mb: 1,
          }}
        >
          Terms of Service
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: `${colors.primary[900]}70`, mb: 5 }}>
          Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>

        <Stack divider={<Divider sx={{ borderColor: colors.primary[100] }} />} spacing={4}>
          {SECTIONS.map((section) => (
            <Box key={section.title}>
              <Typography
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 700,
                  color: colors.primary[900],
                  mb: 1.5,
                }}
              >
                {section.title}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: "0.9375rem",
                  lineHeight: 1.7,
                  color: `${colors.primary[900]}90`,
                }}
              >
                {section.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default TermsOfService;
